import axios from 'axios'
import {A11yAiProvider, buildAltTextFromCaption, PageContext, ProviderCapabilities, Suggestion} from '../aiSuggest'

const BASE_URL = process.env.REACT_APP_API_URL ?? ''

async function blobToBase64DataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

async function post(operation: string, payload: Record<string, unknown>, ctx?: PageContext): Promise<string> {
  const context = ctx ? {pageTitle: ctx.pageTitle, locale: ctx.locale} : undefined
  const res = await axios.post(`${BASE_URL}/a11y/suggest`, {operation, payload, context})
  return res.data?.data?.text ?? ''
}

export function createCmsApiProvider(): A11yAiProvider {
  const capabilities: ProviderCapabilities = {
    altText: true,
    linkText: true,
    headerLabel: true,
    tableCaption: true,
    headingHint: true,
    ariaLabel: true,
  }

  return {
    id: 'cms',
    capabilities,

    async suggestAltText(image: Blob | string, ctx?: PageContext): Promise<Suggestion> {
      const dataUrl = image instanceof Blob ? await blobToBase64DataUrl(image) : image
      const imageMime = image instanceof Blob ? image.type : 'image/jpeg'
      const text = await post('altText', {imageBase64: dataUrl, imageMime}, ctx)
      return {text: buildAltTextFromCaption(text, ctx), confidence: 'high', provider: 'cms'}
    },

    async suggestLinkText(href: string, surroundingText: string, ctx?: PageContext): Promise<Suggestion> {
      const text = await post('linkText', {href, surroundingText}, ctx)
      return {text, confidence: 'high', provider: 'cms'}
    },

    async suggestHeaderLabel(columnSamples: string[], ctx?: PageContext): Promise<Suggestion> {
      const text = await post('headerLabel', {columnSamples}, ctx)
      return {text, confidence: 'high', provider: 'cms'}
    },

    async suggestTableCaption(cellSamples: string[], ctx?: PageContext): Promise<Suggestion> {
      const text = await post('tableCaption', {cellSamples}, ctx)
      return {text, confidence: 'high', provider: 'cms'}
    },

    async suggestHeadingHint(sectionText: string, ctx?: PageContext): Promise<Suggestion> {
      const text = await post('headingHint', {sectionText}, ctx)
      return {text, confidence: 'high', provider: 'cms'}
    },

    async suggestAriaLabel(elementDescription: string, ctx?: PageContext): Promise<Suggestion> {
      const text = await post('ariaLabel', {elementDescription}, ctx)
      return {text, confidence: 'high', provider: 'cms'}
    },
  }
}
